import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ActionIcon, Button, Checkbox, FileInput, Indicator, Modal, MultiSelect, NumberInput, SegmentedControl, Switch, TextInput, Tooltip, UnstyledButton } from '@mantine/core';
import {
  DndContext,
  type DragCancelEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  type DragStartEvent,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DraggableAttributes,
  type DraggableSyntheticListeners,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button as SharedButton } from '../../../../../components/Button';
import { DragHandle } from '../../../../../components/DragHandle/DragHandle';
import { useProductReorderController } from './useProductReorderController';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faPen as faPenDef,
  faLayerGroup as faLayerGroupDef,
  faBan as faBanDef,
  faCircleCheck as faCircleCheckDef,
  faXmark as faXmarkRemoveDef,
  faSort as faSortDef,
  faSortUp as faSortUpDef,
  faSortDown as faSortDownDef,
} from '@fortawesome/free-solid-svg-icons';
import { PrinterView } from '../../../../../services/printers/printers.service';
import { ProductView } from '../../../../../services/products/products.service';
import type { CategoryReportGroup, CategoryView } from '../../../../../services/categories/categories.service';
import { compareCategoryNamesForDisplay } from '../../../../../services/categories/category-order.util';
import type { ProductFormRow, ProductFormState } from '../../../core/pos-shell-page.types';
import { formatMoney } from '../../../../../utils/money';
import { InlineCategoryCreateField } from '../inline-category-create-field/InlineCategoryCreateField';
import { BulkEditProductsModal } from '../bulk-edit-products-modal/BulkEditProductsModal';
import { useProductBulkEdit, type BulkUpdateProductsPayload } from '../bulk-edit-products-modal/useProductBulkEdit';
import { useProductSettingsListController } from './useProductSettingsListController';
import type { KdsFilter, ProductSortKey, StatusFilter } from './product-filter-sort.utils';
import navyModal from '../../../../../components/Modal/navy-modal.module.scss';
import classes from '../shared/settings-shared.module.scss';
import bulkClasses from '../shared/product-settings.module.scss';

const faPen = faPenDef as IconProp;
const faLayerGroup = faLayerGroupDef as IconProp;
const faBan = faBanDef as IconProp;
const faCircleCheck = faCircleCheckDef as IconProp;
const faXmarkRemove = faXmarkRemoveDef as IconProp;
const faSort = faSortDef as IconProp;
const faSortUp = faSortUpDef as IconProp;
const faSortDown = faSortDownDef as IconProp;

const CARD_LONG_PRESS_MS = 500;
const PRODUCTS_COLUMN_WIDTHS_STORAGE_KEY = 'pos.product-settings.column-widths.v1';

type ProductResizableColumnKey =
  | 'name'
  | 'price'
  | 'category'
  | 'image'
  | 'status'
  | 'printers'
  | 'kds'
  | 'actions';

const DEFAULT_COLUMN_WIDTHS: Record<ProductResizableColumnKey, number> = {
  name: 220,
  price: 92,
  category: 170,
  image: 72,
  status: 100,
  printers: 240,
  kds: 72,
  actions: 140,
};

const MIN_COLUMN_WIDTHS: Record<ProductResizableColumnKey, number> = {
  name: 140,
  price: 72,
  category: 120,
  image: 56,
  status: 84,
  printers: 140,
  kds: 56,
  actions: 116,
};

type SortableRowRenderProps = {
  setNodeRef: (element: HTMLElement | null) => void;
  setActivatorNodeRef: (element: HTMLElement | null) => void;
  style: React.CSSProperties;
  attributes: DraggableAttributes;
  listeners: DraggableSyntheticListeners;
  isDragging: boolean;
};

// Architectural summary: dnd-kit sortable wrapper exposing drag props to a row via render-prop.
const SortableRow: React.FC<{ id: number; disabled: boolean; children: (drag: SortableRowRenderProps) => React.ReactNode }> = ({ id, disabled, children }) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : undefined,
  };
  return <>{children({ setNodeRef, setActivatorNodeRef, style, attributes, listeners, isDragging })}</>;
};

type ProductSettingsSectionProps = {
  productFormRows: ProductFormRow[];
  activePrinters: PrinterView[];
  settingsProducts: ProductView[];
  categories: CategoryView[];
  employeeSession: string | null;
  isSavingProduct: boolean;
  editingProductId: number | null;
  isLoadingProducts: boolean;
  isLoadingPrinters: boolean;
  isTogglingProductStatus: boolean;
  onChangeProductFormRowField: (index: number, field: keyof ProductFormState, value: string | number | number[] | boolean | null) => void;
  onProductFormRowImageChange: (index: number, file: File | null) => void;
  onAddProductFormRow: () => void;
  onRemoveProductFormRow: (index: number) => void;
  onSaveProduct: (event: React.FormEvent<HTMLFormElement>) => void;
  onCancelEdit: () => void;
  onRefreshProducts: () => void;
  onExportProducts: () => void;
  onOpenImport: () => void;
  onEditProduct: (product: ProductView) => void;
  onOpenOptions: (product: ProductView) => void;
  variantComboCountByProductId?: Record<number, number>;
  onDeactivateProduct: (productId: number) => void;
  onActivateProduct: (productId: number) => void;
  onToggleProductKds: (productId: number, nextKds: boolean) => void;
  isProductKdsUpdating: (productId: number) => boolean;
  onCreateCategory: (name: string, reportGroup: CategoryReportGroup | null) => Promise<number | null>;
  onBulkUpdateProducts: (payload: BulkUpdateProductsPayload) => Promise<void>;
};

const ProductSettingsSectionComponent: React.FC<ProductSettingsSectionProps> = ({
  productFormRows,
  activePrinters,
  settingsProducts,
  categories,
  employeeSession,
  isSavingProduct,
  editingProductId,
  isLoadingProducts,
  isLoadingPrinters,
  isTogglingProductStatus,
  onChangeProductFormRowField,
  onProductFormRowImageChange,
  onAddProductFormRow,
  onRemoveProductFormRow,
  onSaveProduct,
  onCancelEdit,
  onRefreshProducts,
  onExportProducts,
  onOpenImport,
  onEditProduct,
  onOpenOptions,
  variantComboCountByProductId = {},
  onDeactivateProduct,
  onActivateProduct,
  onToggleProductKds,
  isProductKdsUpdating,
  onCreateCategory,
  onBulkUpdateProducts,
}) => {
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const isModalOpen = isCreateModalOpen || editingProductId !== null;
  const isEditMode = editingProductId !== null;
  const editingProduct = isEditMode
    ? settingsProducts.find((product) => product.id === editingProductId) ?? null
    : null;

  const printerOptions = activePrinters.map((printer) => ({
    value: String(printer.id),
    label: printer.name,
  }));
  const categoryOptions = categories
    .filter((category) => category.isActive)
    .sort((a, b) => compareCategoryNamesForDisplay(a.name, b.name))
    .map((category) => ({ value: String(category.id), label: category.name }));
  const categoryNameById = useMemo(
    () => new Map(categories.map((category) => [category.id, category.name])),
    [categories],
  );
  const printerNameById = useMemo(
    () => new Map(activePrinters.map((printer) => [printer.id, printer.name])),
    [activePrinters],
  );

  const list = useProductSettingsListController({ products: settingsProducts, categoryNameById, printerNameById });
  const { visibleProducts, sortKey, sortDir } = list;

  // Drag disabled only under a column sort; filter/search still allow it via splice.
  const isSortActive = sortKey !== null;
  const isReorderable = !isSortActive;
  const filteredVisibleIds = useMemo(() => visibleProducts.map((product) => product.id), [visibleProducts]);
  const reorder = useProductReorderController({ products: settingsProducts, visibleIds: filteredVisibleIds, employeeSession, enabled: isReorderable });
  const displayProducts = isReorderable ? reorder.orderedVisible : visibleProducts;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragStart = (_event: DragStartEvent) => {
    markSelectionSuppressed();
    handleCardPointerUp();
  };

  const handleDragCancel = (_event: DragCancelEvent) => {
    markSelectionSuppressed();
  };

  const handleDragEnd = (event: DragEndEvent) => {
    markSelectionSuppressed();
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    void reorder.handleDragEnd(Number(active.id), Number(over.id));
  };

  const bulk = useProductBulkEdit({ onBulkUpdate: onBulkUpdateProducts });
  const visibleIds = displayProducts.map((product) => product.id);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => bulk.isSelected(id));
  const someSelected = bulk.selectedCount > 0 && !allSelected;

  const cardLongPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suppressRowClickUntilRef = useRef(0);
  const activeColumnResizeRef = useRef<{
    key: ProductResizableColumnKey;
    startX: number;
    startWidth: number;
    directionFactor: 1 | -1;
  } | null>(null);
  const [columnWidths, setColumnWidths] = useState<Record<ProductResizableColumnKey, number>>(() => {
    if (typeof window === 'undefined') {
      return DEFAULT_COLUMN_WIDTHS;
    }
    try {
      const raw = window.localStorage.getItem(PRODUCTS_COLUMN_WIDTHS_STORAGE_KEY);
      if (!raw) {
        return DEFAULT_COLUMN_WIDTHS;
      }
      const parsed = JSON.parse(raw) as Partial<Record<ProductResizableColumnKey, number>>;
      const merged = { ...DEFAULT_COLUMN_WIDTHS };
      (Object.keys(DEFAULT_COLUMN_WIDTHS) as ProductResizableColumnKey[]).forEach((key) => {
        const next = parsed[key];
        if (typeof next === 'number' && Number.isFinite(next)) {
          merged[key] = Math.max(MIN_COLUMN_WIDTHS[key], Math.round(next));
        }
      });
      return merged;
    } catch {
      return DEFAULT_COLUMN_WIDTHS;
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(PRODUCTS_COLUMN_WIDTHS_STORAGE_KEY, JSON.stringify(columnWidths));
  }, [columnWidths]);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      const activeResize = activeColumnResizeRef.current;
      if (!activeResize) {
        return;
      }
      const { key, startX, startWidth, directionFactor } = activeResize;
      const delta = event.clientX - startX;
      const next = Math.max(MIN_COLUMN_WIDTHS[key], Math.round(startWidth + (delta * directionFactor)));
      setColumnWidths((previous) => (previous[key] === next ? previous : { ...previous, [key]: next }));
    };

    const handleMouseUp = () => {
      activeColumnResizeRef.current = null;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const beginColumnResize = (key: ProductResizableColumnKey) => (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const tableDirection = (event.currentTarget.closest('table') as HTMLTableElement | null)?.dir;
    const direction =
      tableDirection
      || document.documentElement.getAttribute('dir')
      || document.body.getAttribute('dir')
      || window.getComputedStyle(event.currentTarget).direction;
    activeColumnResizeRef.current = {
      key,
      startX: event.clientX,
      startWidth: columnWidths[key],
      directionFactor: direction === 'rtl' ? -1 : 1,
    };
  };

  const markSelectionSuppressed = () => {
    suppressRowClickUntilRef.current = Date.now() + 250;
  };
  const handleCardPointerDown = (productId: number) => (event: React.PointerEvent) => {
    if (event.button !== 0) return;
    const target = event.target;
    if (target instanceof HTMLElement && target.closest('[data-testid^="product-settings-drag-"]')) {
      return;
    }
    cardLongPressTimerRef.current = setTimeout(() => {
      bulk.startSelectionMode(productId);
    }, CARD_LONG_PRESS_MS);
  };
  const handleCardPointerUp = () => {
    if (cardLongPressTimerRef.current) {
      clearTimeout(cardLongPressTimerRef.current);
      cardLongPressTimerRef.current = null;
    }
  };

  const closeModal = () => {
    setCreateModalOpen(false);
    onCancelEdit();
  };

  const preventProductEditorSelection = (event: React.SyntheticEvent<HTMLFormElement>) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) {
      return;
    }
    const end = target.value.length;
    if (target.selectionStart !== end || target.selectionEnd !== end) {
      target.setSelectionRange(end, end);
    }
  };

  // isCreateModalOpen is local state, not derived from productFormRows — close it
  // here on the same success signal resetProductForm() already uses (rows collapsed
  // back to one empty row), so a failed/blocked save leaves the modal open.
  const wasSavingProductRef = useRef(false);
  useEffect(() => {
    if (
      wasSavingProductRef.current &&
      !isSavingProduct &&
      productFormRows.length === 1 &&
      productFormRows[0].name === '' &&
      isCreateModalOpen
    ) {
      setCreateModalOpen(false);
    }
    wasSavingProductRef.current = isSavingProduct;
  }, [isSavingProduct, productFormRows, isCreateModalOpen]);

  const renderColumnResizeHandle = (key: ProductResizableColumnKey) => (
    <span
      role="separator"
      aria-orientation="vertical"
      aria-label={`Resize ${key} column`}
      data-testid={`product-settings-resize-${key}`}
      className={bulkClasses.columnResizeHandle}
      onMouseDown={beginColumnResize(key)}
      onClick={(event) => event.stopPropagation()}
    />
  );

  const renderSortHeader = (sortColumn: ProductSortKey, widthColumn: ProductResizableColumnKey, label: string) => (
    <th aria-sort={sortKey === sortColumn ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <div className={bulkClasses.resizableHeaderWrap}>
        <UnstyledButton
          className={bulkClasses.sortHeader}
          data-testid={`product-settings-sort-${sortColumn}`}
          onClick={() => list.toggleSort(sortColumn)}
        >
          <span>{label}</span>
          <FontAwesomeIcon
            className={bulkClasses.sortCaret}
            icon={sortKey === sortColumn ? (sortDir === 'asc' ? faSortUp : faSortDown) : faSort}
          />
        </UnstyledButton>
        {renderColumnResizeHandle(widthColumn)}
      </div>
    </th>
  );

  const renderPlainHeader = (label: string, key: ProductResizableColumnKey) => (
    <th>
      <div className={bulkClasses.resizableHeaderWrap}>
        <span>{label}</span>
        {renderColumnResizeHandle(key)}
      </div>
    </th>
  );

  return (
    <section className={classes.section}>
      <div className={classes.toolbar}>
        <SharedButton type="button" variant="save" size="sm" onClick={() => setCreateModalOpen(true)} dataTestId="product-settings-create-open">
          צור מוצר
        </SharedButton>
        <div className={classes.formActions}>
          <Button
            type="button"
            variant="default"
            radius="md"
            data-testid="product-settings-refresh"
            onClick={onRefreshProducts}
            disabled={isLoadingProducts || isLoadingPrinters}
          >
            {isLoadingProducts || isLoadingPrinters ? 'מרענן...' : 'רענן'}
          </Button>
          <Button type="button" variant="default" radius="md" data-testid="product-settings-export" onClick={onExportProducts} disabled={isLoadingProducts}>
            ייצוא
          </Button>
          <Button type="button" variant="default" radius="md" data-testid="product-settings-import" onClick={onOpenImport}>
            ייבוא
          </Button>
        </div>
      </div>

      <TextInput
        data-testid="product-settings-search"
        placeholder="חיפוש לפי שם, מחיר או קטגוריה"
        value={list.searchQuery}
        onChange={(event) => list.setSearchQuery(event.currentTarget.value)}
        onContextMenu={(event) => event.preventDefault()}
        onSelect={(event) => {
          const input = event.currentTarget;
          const end = input.value.length;
          if (input.selectionStart !== end || input.selectionEnd !== end) {
            input.setSelectionRange(end, end);
          }
        }}
        radius="md"
        aria-label="חיפוש מוצרים"
      />

      <div className={bulkClasses.filterToolbar} dir="rtl">
        <MultiSelect
          data-testid="product-settings-filter-category"
          label="קטגוריות"
          placeholder="כל הקטגוריות"
          data={categoryOptions}
          value={list.filters.categoryIds.map((id) => String(id))}
          onChange={(values) => list.setCategoryIds(values.map((value) => Number(value)))}
          radius="md"
          searchable
          clearable
          className={bulkClasses.filterField}
        />
        <MultiSelect
          data-testid="product-settings-filter-printer"
          label="מדפסות"
          placeholder="כל המדפסות"
          data={printerOptions}
          value={list.filters.printerIds.map((id) => String(id))}
          onChange={(values) => list.setPrinterIds(values.map((value) => Number(value)))}
          radius="md"
          searchable
          clearable
          className={bulkClasses.filterField}
        />
        <div className={bulkClasses.filterSegment}>
          <span className={bulkClasses.filterLabel}>KDS</span>
          <SegmentedControl
            data-testid="product-settings-filter-kds"
            value={list.filters.kds}
            onChange={(value) => list.setKds(value as KdsFilter)}
            data={[
              { value: 'all', label: 'הכל' },
              { value: 'on', label: 'פעיל' },
              { value: 'off', label: 'כבוי' },
            ]}
          />
        </div>
        <div className={bulkClasses.filterSegment}>
          <span className={bulkClasses.filterLabel}>סטטוס</span>
          <SegmentedControl
            data-testid="product-settings-filter-status"
            value={list.filters.status}
            onChange={(value) => list.setStatus(value as StatusFilter)}
            data={[
              { value: 'all', label: 'הכל' },
              { value: 'active', label: 'פעיל' },
              { value: 'inactive', label: 'לא פעיל' },
            ]}
          />
        </div>
        <Button
          type="button"
          variant="default"
          radius="md"
          data-testid="product-settings-clear-filters"
          onClick={list.clearFilters}
          disabled={!list.hasActiveFilters}
        >
          נקה סינון
        </Button>
      </div>

      {bulk.selectedCount > 0 && (
        <div className={bulkClasses.bulkBar}>
          <span className={bulkClasses.bulkCount}>{bulk.selectedCount} נבחרו</span>
          <div className={bulkClasses.bulkActions}>
            <SharedButton variant="save" size="sm" onClick={bulk.openModal} dataTestId="product-settings-bulk-edit">
              עריכה מרובה
            </SharedButton>
            <Button type="button" variant="default" radius="md" data-testid="product-settings-clear-selection" onClick={bulk.clearSelection}>
              נקה
            </Button>
          </div>
        </div>
      )}

      <div className={bulkClasses.selectAllMobile}>
        <Checkbox
          data-testid="product-settings-select-all-mobile"
          label="בחר הכל"
          checked={allSelected}
          indeterminate={someSelected}
          onChange={(event) => bulk.setAll(visibleIds, event.currentTarget.checked)}
        />
      </div>

      <div className={`${classes.tableWrap} ${bulkClasses.productsTableWrap}`}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragCancel={handleDragCancel}
          onDragEnd={handleDragEnd}
        >
        <table className={`${classes.table} ${bulkClasses.productsTable}`}>
          <colgroup>
            <col style={{ width: 36 }} />
            <col style={{ width: 40 }} />
            <col data-testid="product-settings-col-name" style={{ width: columnWidths.name }} />
            <col data-testid="product-settings-col-price" style={{ width: columnWidths.price }} />
            <col data-testid="product-settings-col-category" style={{ width: columnWidths.category }} />
            <col data-testid="product-settings-col-image" style={{ width: columnWidths.image }} />
            <col data-testid="product-settings-col-status" style={{ width: columnWidths.status }} />
            <col data-testid="product-settings-col-printers" style={{ width: columnWidths.printers }} />
            <col data-testid="product-settings-col-kds" style={{ width: columnWidths.kds }} />
            <col data-testid="product-settings-col-actions" style={{ width: columnWidths.actions }} />
          </colgroup>
          <thead>
            <tr>
              <th className={bulkClasses.dragCell} aria-hidden />
              <th className={bulkClasses.selectCell}>
                <Checkbox
                  data-testid="product-settings-select-all"
                  aria-label="בחר הכל (טבלה)"
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={(event) => bulk.setAll(visibleIds, event.currentTarget.checked)}
                />
              </th>
              {renderSortHeader('name', 'name', 'שם')}
              {renderSortHeader('price', 'price', 'מחיר')}
              {renderSortHeader('category', 'category', 'קטגוריה')}
              {renderPlainHeader('תמונה', 'image')}
              {renderSortHeader('status', 'status', 'סטטוס')}
              {renderSortHeader('printers', 'printers', 'מדפסות')}
              {renderSortHeader('kds', 'kds', 'KDS')}
              {renderPlainHeader('פעולות', 'actions')}
            </tr>
          </thead>
          <SortableContext items={visibleIds} strategy={verticalListSortingStrategy}>
          <tbody>
            {displayProducts.map((product) => (
              <SortableRow key={product.id} id={product.id} disabled={!isReorderable}>
                {(drag) => (
              <tr
                ref={drag.setNodeRef}
                style={drag.style}
                data-testid={`product-settings-row-${product.id}`}
                className={bulk.isSelectionMode && bulk.isSelected(product.id) ? bulkClasses.cardSelected : ''}
                onPointerDown={handleCardPointerDown(product.id)}
                onPointerUp={handleCardPointerUp}
                onPointerLeave={handleCardPointerUp}
                onClick={() => {
                  if (Date.now() < suppressRowClickUntilRef.current) {
                    return;
                  }
                  if (bulk.isSelectionMode) {
                    bulk.toggle(product.id);
                  }
                }}
              >
                <td className={bulkClasses.dragCell} data-label="" onClick={(event) => event.stopPropagation()}>
                  <DragHandle
                    dataTestId={`product-settings-drag-${product.id}`}
                    disabled={!isReorderable}
                    attributes={drag.attributes}
                    listeners={isReorderable ? drag.listeners : undefined}
                    setActivatorNodeRef={drag.setActivatorNodeRef}
                    onClick={isSortActive ? list.clearSort : undefined}
                    title={isSortActive ? 'לחיצה מנקה מיון ומאפשרת גרירה' : undefined}
                  />
                </td>
                <td className={bulkClasses.selectCell} data-label="בחר" onClick={(event) => event.stopPropagation()}>
                  <Checkbox
                    data-testid={`product-settings-select-${product.id}`}
                    aria-label={`בחר ${product.name}`}
                    checked={bulk.isSelected(product.id)}
                    onChange={() => bulk.toggle(product.id)}
                  />
                </td>
                <td data-label="שם">{product.name}</td>
                <td data-label="מחיר">{formatMoney(product.price)}</td>
                <td data-label="קטגוריה">{product.categoryId !== null ? categoryNameById.get(product.categoryId) ?? '—' : '—'}</td>
                <td data-label="תמונה">
                  {product.imageUrl ? (
                    <img src={product.imageUrl} alt={product.name} className={classes.thumb} loading="lazy" />
                  ) : (
                    <span className={classes.thumbPlaceholder}>ללא</span>
                  )}
                </td>
                <td data-label="סטטוס">
                  <span className={product.isActive ? classes.statusActive : classes.statusInactive}>
                    {product.isActive ? 'פעיל' : 'לא פעיל'}
                  </span>
                </td>
                <td
                  data-label="מדפסות"
                  className={product.printerIds.length === 0 ? bulkClasses.emptyOnMobile : ''}
                >
                  {product.printerIds.length > 0
                    ? product.printerIds
                      .map((printerId) => activePrinters.find((printer) => printer.id === printerId)?.name ?? `#${printerId}`)
                      .join(', ')
                    : '—'}
                </td>
                <td data-label="KDS">
                  <Checkbox
                    data-testid={`product-settings-kds-${product.id}`}
                    aria-label="KDS"
                    checked={product.kds}
                    onChange={(event) => onToggleProductKds(product.id, event.currentTarget.checked)}
                    disabled={isProductKdsUpdating(product.id) || isTogglingProductStatus}
                  />
                </td>
                <td className={bulkClasses.actionsCellTD} onClick={(event) => event.stopPropagation()}>
                  <div className={`${classes.actionsCell} ${bulkClasses.productActionsCell}`}>
                    <Tooltip label="ערוך" withArrow>
                      <ActionIcon
                        aria-label="ערוך"
                        data-testid={`product-settings-edit-${product.id}`}
                        variant="subtle"
                        color="gray"
                        radius="md"
                        size="lg"
                        onClick={() => onEditProduct(product)}
                      >
                        <FontAwesomeIcon icon={faPen} />
                      </ActionIcon>
                    </Tooltip>

                    <Tooltip label="וריאציות וקומבו" withArrow>
                      <Indicator
                        label={variantComboCountByProductId[product.id] ?? 0}
                        disabled={!(variantComboCountByProductId[product.id] ?? 0)}
                        color="blue"
                        size={18}
                        offset={4}
                      >
                        <ActionIcon
                          aria-label="וריאציות וקומבו"
                          data-testid={`product-settings-options-${product.id}`}
                          variant="subtle"
                          color="blue"
                          radius="md"
                          size="lg"
                          onClick={() => onOpenOptions(product)}
                        >
                          <FontAwesomeIcon icon={faLayerGroup} />
                        </ActionIcon>
                      </Indicator>
                    </Tooltip>

                    {product.isActive ? (
                      <Tooltip label="השבת" withArrow>
                        <ActionIcon
                          aria-label="השבת"
                          data-testid={`product-settings-toggle-${product.id}`}
                          variant="subtle"
                          color="red"
                          radius="md"
                          size="lg"
                          className={bulkClasses.statusActionIcon}
                          onClick={() => onDeactivateProduct(product.id)}
                          disabled={isTogglingProductStatus}
                        >
                          <FontAwesomeIcon icon={faBan} />
                        </ActionIcon>
                      </Tooltip>
                    ) : (
                      <Tooltip label="הפעל" withArrow>
                        <ActionIcon
                          aria-label="הפעל"
                          data-testid={`product-settings-toggle-${product.id}`}
                          variant="subtle"
                          color="green"
                          radius="md"
                          size="lg"
                          className={bulkClasses.statusActionIcon}
                          onClick={() => onActivateProduct(product.id)}
                          disabled={isTogglingProductStatus}
                        >
                          <FontAwesomeIcon icon={faCircleCheck} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </div>
                </td>
              </tr>
                )}
              </SortableRow>
            ))}
            {displayProducts.length === 0 && !isLoadingProducts && (
              <tr>
                <td colSpan={10} className={classes.empty}>
                  {settingsProducts.length === 0 ? 'אין מוצרים לטננט זה עדיין.' : 'לא נמצאו מוצרים תואמים לחיפוש.'}
                </td>
              </tr>
            )}
          </tbody>
          </SortableContext>
        </table>
        </DndContext>
      </div>

      <Modal
        opened={isModalOpen}
        onClose={closeModal}
        onContextMenu={(event) => event.preventDefault()}
        title={<span onContextMenu={(event) => event.preventDefault()}>{isEditMode ? 'עריכת מוצר' : 'מוצר חדש'}</span>}
        size="lg"
        centered
        radius="md"
        classNames={{ root: `${navyModal.root} ${bulkClasses.productEditorModal}` }}
        closeButtonProps={{ disabled: isSavingProduct, 'aria-label': 'סגור', 'data-testid': 'product-settings-form-close' } as React.ComponentProps<typeof Modal>['closeButtonProps']}
        closeOnClickOutside={!isSavingProduct}
        closeOnEscape={!isSavingProduct}
      >
        <form
          onSubmit={onSaveProduct}
          onContextMenu={(event) => event.preventDefault()}
          onSelect={preventProductEditorSelection}
          className={classes.section}
        >
          {productFormRows.map((row, index) => (
            <div key={row.rowKey} className={bulkClasses.productFormRow}>
              {index > 0 && (
                <div className={bulkClasses.productFormRowHeader}>
                  <ActionIcon
                    aria-label="הסר מוצר"
                    data-testid={`product-settings-form-remove-${index}`}
                    variant="subtle"
                    color="red"
                    radius="md"
                    size="sm"
                    disabled={isSavingProduct}
                    onClick={() => onRemoveProductFormRow(index)}
                  >
                    <FontAwesomeIcon icon={faXmarkRemove} />
                  </ActionIcon>
                </div>
              )}
              <div className={classes.formRow}>
                <TextInput
                  data-testid={`product-settings-form-name-${index}`}
                  label="שם המוצר"
                  placeholder="שם המוצר"
                  value={row.name}
                  onChange={(event) => onChangeProductFormRowField(index, 'name', event.currentTarget.value)}
                  minLength={1}
                  maxLength={120}
                  required
                  radius="md"
                />
                <NumberInput
                  data-testid={`product-settings-form-price-${index}`}
                  label="מחיר"
                  placeholder="מחיר"
                  value={row.price === '' ? '' : Number(row.price)}
                  onChange={(value) => onChangeProductFormRowField(index, 'price', value === '' || value === null || value === undefined ? '' : String(value))}
                  min={0}
                  decimalScale={1}
                  required
                  radius="md"
                />
                <InlineCategoryCreateField
                  options={categoryOptions}
                  value={row.categoryId}
                  onChange={(categoryId) => onChangeProductFormRowField(index, 'categoryId', categoryId)}
                  onCreateCategory={onCreateCategory}
                  disabled={isSavingProduct}
                />
                <MultiSelect
                  data-testid={`product-settings-form-printers-${index}`}
                  label="שיוך מדפסות"
                  placeholder="בחר מדפסות"
                  data={printerOptions}
                  value={row.printerIds.map((id) => String(id))}
                  onChange={(values) => onChangeProductFormRowField(index, 'printerIds', values.map((value) => Number(value)))}
                  radius="md"
                  searchable
                  clearable
                  classNames={{ root: bulkClasses.printerSelectWrapper }}
                />
                <div className={classes.fileField}>
                  <span className={classes.fileFieldLabel}>תמונת מוצר</span>
                  <FileInput
                    data-testid={`product-settings-form-image-${index}`}
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(file) => onProductFormRowImageChange(index, file)}
                    value={row.imageFile}
                    clearable
                    placeholder="JPEG / PNG / WebP עד 3MB"
                  />
                  {row.imageFile && <span className={classes.lead}>תמונה נבחרה: {row.imageFile.name}</span>}
                </div>
                <div className={bulkClasses.kdsField}>
                  <Checkbox
                    data-testid={`product-settings-form-kds-${index}`}
                    label="KDS"
                    checked={row.kds}
                    onChange={(event) => onChangeProductFormRowField(index, 'kds', event.currentTarget.checked)}
                  />
                </div>
              </div>
            </div>
          ))}
          <div className={isEditMode ? bulkClasses.editFormFooter : bulkClasses.productFormFooter}>
            {isEditMode && editingProduct && (
              <Switch
                data-testid="product-settings-status-switch"
                aria-label={editingProduct.isActive ? 'פעיל' : 'לא פעיל'}
                onLabel="פעיל"
                offLabel="לא פעיל"
                size="lg"
                checked={editingProduct.isActive}
                color="green"
                className={bulkClasses.statusSwitch}
                onChange={(event) =>
                  event.currentTarget.checked
                    ? onActivateProduct(editingProduct.id)
                    : onDeactivateProduct(editingProduct.id)
                }
                disabled={isTogglingProductStatus}
              />
            )}
            {!isEditMode && (
              <Button
                type="button"
                variant="default"
                radius="md"
                className={bulkClasses.addRowButton}
                data-testid="product-settings-form-add-row"
                onClick={onAddProductFormRow}
                disabled={isSavingProduct}
              >
                הוסף מוצר נוסף
              </Button>
            )}
            <SharedButton type="submit" variant="save" size="sm" disabled={isSavingProduct} dataTestId="product-settings-form-submit">
              {isSavingProduct ? 'שומר...' : 'שמור'}
            </SharedButton>
          </div>
        </form>
      </Modal>

      <BulkEditProductsModal
        opened={bulk.isModalOpen}
        onClose={bulk.closeModal}
        selectedCount={bulk.selectedCount}
        categoryOptions={categoryOptions}
        printerOptions={printerOptions}
        isSubmitting={bulk.isSubmitting}
        onSubmit={(fields) => void bulk.submit(fields)}
      />
    </section>
  );
};

export const ProductSettingsSection = React.memo(ProductSettingsSectionComponent);
